import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { PlusCircle, Trash2, Bath, Loader2 } from 'lucide-react';
import { filterBanos } from '@/services/banos.service';

const BanosSelector = ({ value = [], onChange, disabled = false }) => {
  const [banosDisponibles, setBanosDisponibles] = useState([]);
  const [loadingBanos, setLoadingBanos] = useState(false);
  const [selectedBanoId, setSelectedBanoId] = useState('');
  const [cantidad, setCantidad] = useState(1);

  useEffect(() => {
    loadBanos();
  }, []);

  const loadBanos = async () => {
    setLoadingBanos(true);
    try {
      const response = await filterBanos({ limit: 1000, offset: 1, query: { estado: 'Activo' } });
      setBanosDisponibles(response.data || []);
    } catch (error) {
      console.error('Error al cargar baños:', error);
    } finally {
      setLoadingBanos(false);
    }
  };

  const handleAddBano = () => {
    if (!selectedBanoId || cantidad < 1) return;

    const banoSeleccionado = banosDisponibles.find(b => b._id === selectedBanoId);
    if (!banoSeleccionado) return;

    // Verificar si ya existe
    const yaExiste = value.some(b => b.banoId === selectedBanoId);
    if (yaExiste) {
      return;
    }

    const nuevoBano = {
      banoId: banoSeleccionado._id,
      nombre: banoSeleccionado.nombre,
      cantidad: parseInt(cantidad)
    };

    onChange([...value, nuevoBano]);
    setSelectedBanoId('');
    setCantidad(1);
  };

  const handleRemoveBano = (banoId) => {
    onChange(value.filter(b => b.banoId !== banoId));
  };

  const handleUpdateCantidad = (banoId, nuevaCantidad) => {
    onChange(value.map(b => 
      b.banoId === banoId 
        ? { ...b, cantidad: parseInt(nuevaCantidad) || 1 }
        : b
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Bath className="h-5 w-5 text-teal-600" />
          Tipos de Baños
        </Label>
        <span className="text-sm text-gray-500">
          {value.length} tipo(s) de baño
        </span>
      </div>

      {/* Formulario para agregar cama */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          {loadingBanos ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
              <span className="ml-2 text-sm text-gray-600">Cargando baños...</span>
            </div>
          ) : banosDisponibles.length === 0 ? (
            <div className="text-center py-4 text-sm text-gray-500">
              No hay tipos de baños disponibles.
              <br />
              Por favor, crea tipos de baños primero en Parametrización → Baños.
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-7 space-y-2">
                <Label htmlFor="bano-select" className="text-sm">Tipo de Baño</Label>
                <Select 
                  value={selectedBanoId} 
                  onValueChange={setSelectedBanoId}
                  disabled={disabled}
                >
                  <SelectTrigger id="bano-select">
                    <SelectValue placeholder="Selecciona un tipo de baño..." />
                  </SelectTrigger>
                  <SelectContent>
                    {banosDisponibles.map((bano) => (
                      <SelectItem key={bano._id} value={bano._id}>
                        {bano.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-4 space-y-2">
                <Label htmlFor="cantidad-bano" className="text-sm">Cantidad</Label>
                <Input
                  id="cantidad-bano"
                  type="number"
                  min="1"
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  placeholder="1"
                  disabled={disabled}
                />
              </div>

              <div className="col-span-1">
                <Button
                  type="button"
                  onClick={handleAddBano}
                  size="sm"
                  className="w-full"
                  disabled={!selectedBanoId || cantidad < 1 || disabled}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de baños agregados */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((bano, index) => (
            <Card key={bano.banoId} className="border-l-4 border-l-teal-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <Bath className="h-5 w-5 text-teal-600" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{bano.nombre}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-600">Cantidad:</Label>
                      <Input
                        type="number"
                        min="1"
                        value={bano.cantidad}
                        onChange={(e) => handleUpdateCantidad(bano.banoId, e.target.value)}
                        className="w-20 h-8 text-sm"
                        disabled={disabled}
                      />
                    </div>
                    
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBano(bano.banoId)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {value.length === 0 && !loadingBanos && (
        <div className="text-center py-6 text-sm text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
          No hay tipos de baños agregados
        </div>
      )}
    </div>
  );
};

export default BanosSelector;
